import { apiRequest, getRolePreview } from "../../../../shared/api/client";
import type {
  RequestAttachment,
  RequestComment,
  RequestHistoryEntry,
  RequestWorkspaceOffer,
  RequestWorkspaceDetail,
} from "../../domain/models";
import type { RequestWorkspaceRepository } from "../../application/queries/request-workspace";

type RawComment = {
  id: string;
  author: string;
  body: string;
  visibility: string;
  created_at: string;
};

type RawAttachment = {
  id: string;
  original_name: string;
  content_type: string;
  size_bytes: string | number;
  malware_scan_status: string;
  created_at: string;
  offer_no?: string | null;
  uploaded_by_name?: string | null;
  uploaded_by_role?: string | null;
};

type RawHistoryEntry = {
  id: string;
  action: string;
  actor: string | null;
  outcome: string;
  occurred_at: string;
};

type RawOffer = {
  offer_no: string;
  supplier: string;
};

type RawRequestWorkspaceDetail = Omit<
  RequestWorkspaceDetail,
  "comments" | "offers" | "attachments" | "history"
> & {
  comments: RawComment[];
  offers?: RawOffer[];
  attachments: RawAttachment[];
  history: RawHistoryEntry[];
};

function mapComment(comment: RawComment): RequestComment {
  return { ...comment, createdAt: comment.created_at };
}

function mapAttachment(attachment: RawAttachment): RequestAttachment {
  return {
    id: attachment.id,
    name: attachment.original_name,
    contentType: attachment.content_type,
    sizeBytes: Number(attachment.size_bytes),
    scanStatus: attachment.malware_scan_status,
    createdAt: attachment.created_at,
    downloadUrl: `${getRolePreview() ? "/api/demo" : "/api"}/attachments/${encodeURIComponent(attachment.id)}`,
    offerNo: attachment.offer_no ?? null,
    uploadedBy: attachment.uploaded_by_name ?? null,
    uploadedByRole: attachment.uploaded_by_role ?? null,
  };
}

function mapHistory(entry: RawHistoryEntry): RequestHistoryEntry {
  return {
    id: entry.id,
    action: entry.action,
    actor: entry.actor,
    outcome: entry.outcome,
    occurredAt: entry.occurred_at,
  };
}

function mapOffer(offer: RawOffer): RequestWorkspaceOffer {
  return { offerNo: offer.offer_no, supplier: offer.supplier };
}

export const procurementQueryGateway: RequestWorkspaceRepository = {
  async get(requestNo) {
    const detail = await apiRequest<RawRequestWorkspaceDetail>(
      `/api/requests/${encodeURIComponent(requestNo)}`,
    );
    return {
      ...detail,
      comments: detail.comments.map(mapComment),
      offers: detail.offers?.map(mapOffer) ?? [],
      attachments: detail.attachments.map(mapAttachment),
      history: detail.history.map(mapHistory),
    };
  },
  async addComment(requestNo, body) {
    const comment = await apiRequest<RawComment>(
      `/api/requests/${encodeURIComponent(requestNo)}/comments`,
      { method: "POST", body: JSON.stringify({ body }) },
    );
    return mapComment(comment);
  },
  async uploadAttachment(requestNo, file) {
    const body = new FormData();
    body.append("file", file);
    const attachment = await apiRequest<RawAttachment>(
      `/api/requests/${encodeURIComponent(requestNo)}/attachments`,
      { method: "POST", body },
    );
    return mapAttachment(attachment);
  },
};
